<template>
  <modal :open="internalOpen" :width="1400">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading || checkFilterLoading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>
    <div class="mt-8">
      <div class="flex flex-col w-full">
        <form-card
          :title="$t(tKey + '.general.title')"
          :description="$t(tKey + '.general.description')"
        >
          <div class="flex flex-col gap-y-5">
            <FormInput
              v-model="rawNotificationPlan.name"
              :error="errors?.name"
              class="col-start-1 col-end-2"
              type="text"
              :label="$t('notificationPlan.name')"
              :placeholder="$t('notificationPlan.name')"
            />
            <Switch
              v-model="rawNotificationPlan.active"
              :error="errors?.active"
              :label="$t('notificationPlan.active.DEFAULT')"
              :options="{
                yes: $t('notificationPlan.active.true'),
                no: $t('notificationPlan.active.false'),
              }"
            />
            <MultiSwitch
              v-model="typeSwitchValue"
              :label="$t('pages.notificationList.modal.general.typeSwitcher.type')"
              :options="typeSwitchOptions"
            />
          </div>
        </form-card>
        <form-card
          :title="$t(`pages.notificationList.modal.${campaignReferenceTKey}.title`)"
        >
          <template #description>
            {{ $t(`pages.notificationList.modal.${campaignReferenceTKey}.description`) }}
            <CheckFilter
              v-if="rawNotificationPlan.campaignSwitchValue === 'filter'"
              :filter="rawNotificationPlan.campaignFilter"
              :validation-function="campaignFilter?.validateData"
              :license-mode="false"
              route="/filters/notification-campaign"
              class="mt-4"
              :label="$t('pages.notificationList.modal.campaignFilter.countLabel')"
            />
            <CheckFilter
              v-else-if="rawNotificationPlan.campaignSwitchValue === 'filter_license'"
              :filter="rawNotificationPlan.campaignFilter"
              :validation-function="campaignFilter?.validateData"
              :license-mode="true"
              route="/filters/notification-license"
              class="mt-4"
              :label="$t('pages.notificationList.modal.licenseFilter.countLabel')"
            />
          </template>
          <MultiSwitch
            :model-value="rawNotificationPlan.campaignSwitchValue"
            :options="campaignSwitchOptions"
            @update:model-value="updateCampaignSwitchValue"
          />
          <div v-if="rawNotificationPlan.campaignSwitchValue === 'fix'" class="flex flex-col gap-y-5 mt-5">
            <multiselect-tags
              v-model="rawNotificationPlan.campaigns"
              :label="$t('notificationPlan.campaigns')"
              :options="fetchCampaigns"
              :object="false"
              :searchable="true"
              :min-chars="2"
              :filterResults="false"
              :clear-on-select="true"
              :delay="300"
              :resolve-on-load="true"
              :async="true"
            />
          </div>
          <div v-else-if="rawNotificationPlan.campaignSwitchValue === 'filter'" class="flex flex-col gap-y-5 mt-5">
            <span class="font-bold">{{
              t(
                `${tKey}.campaign.text.${
                  !rawNotificationPlan.campaignFilter.logicGroups.length > 0 ? 'all' : 'filter'
                }`
              )
            }}</span>
            <LogicGate
              ref="campaignFilter"
              v-model="rawNotificationPlan.campaignFilter"
              route="/filters/notification-campaign"
            />
          </div>
          <div v-else-if="rawNotificationPlan.campaignSwitchValue === 'fix_license'" class="flex flex-col gap-y-5 mt-5">
            <multiselect-tags
              v-model="rawNotificationPlan.licenses"
              :label="$t('notificationPlan.licenses')"
              :options="fetchLicenses"
              :object="false"
              :searchable="true"
              :min-chars="2"
              :filterResults="false"
              :clear-on-select="true"
              :delay="300"
              :resolve-on-load="true"
              :async="true"
            />
          </div>
          <div v-else-if="rawNotificationPlan.campaignSwitchValue === 'filter_license'" class="flex flex-col gap-y-5 mt-5">
            <span class="font-bold">{{
              t(
                `pages.notificationList.modal.${campaignReferenceTKey}.text.${
                  (rawNotificationPlan?.campaignFilter?.logicGroups?.length ?? 0) > 0 ? 'filter' : 'all'
                }`
              )
            }}</span>
            <LogicGate
              ref="campaignFilter"
              v-model="rawNotificationPlan.campaignFilter"
              route="/filters/notification-license"
            />
          </div>
        </form-card>
        <form-card
          :title="$t(`pages.notificationList.modal.${typeSwitchValue === 'triggered' ? 'filterTriggered' : 'filter'}.title`)"
        >
          <template #description>
            {{ $t(`pages.notificationList.modal.${typeSwitchValue === 'triggered' ? 'filterTriggered' : 'filter'}.description`) }}
            <CheckFilter
              v-model:warning="driverCheckWarning"
              :filter="rawNotificationPlan.receiversFilter"
              :validation-function="driverFilterVerification"
              :additional-payload="checkDriverFilterAdditionalPayload"
              :license-mode="['fix_license', 'filter_license'].includes(rawNotificationPlan.campaignSwitchValue)"
              route="/filters/notification-driver"
              class="mt-4"
              :label="$t('pages.notificationList.modal.driverFilter.countLabel')"
            />
          </template>
          <div class="flex flex-col gap-y-5">
            <span class="font-bold">{{
              t(
                `${tKey}.filter.text.${
                  !rawNotificationPlan.receiversFilter.logicGroups.length > 0 ? 'all' : 'filter'
                }`
              )
            }}</span>
            <LogicGate
              ref="driverFilter"
              v-model="rawNotificationPlan.receiversFilter"
              route="/filters/notification-driver"
              :hide-conditional="['no', 'fix_license', 'filter_license'].includes(rawNotificationPlan.campaignSwitchValue)"
              :hide-conditional-license="['no', 'fix', 'filter'].includes(rawNotificationPlan.campaignSwitchValue)"
            />
          </div>
        </form-card>
        <form-card
          :title="$t(`pages.notificationList.modal.${typeSwitchValue === 'triggered' ? 'dateTriggered' : 'date'}.title`)"
          :description="$t(`pages.notificationList.modal.${typeSwitchValue === 'triggered' ? 'dateTriggered' : 'date'}.description`)"
        >
          <div v-if="typeSwitchValue === 'triggered'" class="flex flex-col gap-y-5 pt-5">
            <Multiselect
              v-model="rawNotificationPlan.dateTriggered.trigger"
              :options="notificationTriggerOptions"
              :groups="rawNotificationPlan.campaignSwitchValue !== 'no'"
              :label="$t('notificationPlan.dateTriggered.trigger')"
              :can-deselect="false"
              :can-clear="false"
            />
            <FormInput
              v-model="rawNotificationPlan.dateTriggered.modifier"
              type="number"
              :label="$t('notificationPlan.dateTriggered.modifier')"
              :placeholder="$t('notificationPlan.dateTriggered.modifier')"
            />
            <p
              v-if="triggeredShowWarningForCampaigns"
              class="text-red"
            >
              {{ $t('pages.notificationList.modal.dateTriggered.warningMessage') }}
            </p>
          </div>
          <template v-else>
            <MultiSwitch
              v-model="dateSwitchValue"
              :options="dateSwitchOptions"
            />
            <div v-if="dateSwitchValue === 'fix'" class="flex flex-col gap-y-5 pt-5">
              <MultiDatePicker v-model="rawNotificationPlan.dateFix.dates" />
            </div>
            <div v-else-if="dateSwitchValue === 'recurrent'" class="flex flex-col gap-y-5 pt-5">
              <date-picker
                v-model="rawNotificationPlan.dateRecurrent.startDate"
                :label="$t('notificationPlan.dateRecurrent.startDate')"
                placeholder-from=""
                mode="dateTime"
              />
              <Multiselect
                v-model="rawNotificationPlan.dateRecurrent.recurrence"
                :options="enumOptionsProvider('/enums/NotificationRecurrenceType')"
                :label="$t('notificationPlan.dateRecurrent.recurrence')"
                :can-deselect="false"
                :can-clear="false"
              />
              <date-picker
                v-model="rawNotificationPlan.dateRecurrent.endDate"
                :label="$t('notificationPlan.dateRecurrent.endDate')"
                placeholder-from=""
                mode="dateTime"
              />
            </div>
            <div v-else-if="dateSwitchValue === 'event'" class="flex flex-col gap-y-5 pt-5">
              <slider
                v-model="rawNotificationPlan.dateEvent.basePercentage"
                :label="$t('notificationPlan.dateEvent.basePercentage')"
              >
                <div class="flex justify-between mt-3">
                  <span class="block">{{ $t('notificationPlan.dateEvent.start') }}</span>
                  <span class="block">{{ $t('notificationPlan.dateEvent.end') }}</span>
                </div>
              </slider>
              <FormInput
                v-model="rawNotificationPlan.dateEvent.modifier"
                type="number"
                :label="$t('notificationPlan.dateEvent.modifier')"
                :placeholder="$t('notificationPlan.dateEvent.modifier')"
              />
            </div>
          </template>
        </form-card>
        <form-card
          :title="$t(tKey + '.message.title')"
          :description="$t(tKey + '.message.description')"
        >
          <div class="flex flex-col">
            <Checkbox
              v-model="rawNotificationPlan.sms.checked"
              :label="t('notificationPlan.transports.sms')"
              value="sms"
            />
            <translation-accordion
              v-if="rawNotificationPlan.sms.checked"
              v-slot="{ translation }"
              v-model="rawNotificationPlan.sms"
              class="translation-accordion-style"
            >
              <div class="flex flex-col gap-y-5">
                <TextArea
                  v-model="translation.message"
                  type="text"
                  name="sms-message"
                  :error="errors?.title"
                  :label="$t('notificationPlanTranslation.message')"
                />
                <div>
                  {{ countCharacters(translation?.message ?? '') }} / {{ maximumCharacters(translation?.message ?? '') }}
                </div>
              </div>
            </translation-accordion>
            <Checkbox
              v-model="rawNotificationPlan.push.checked"
              :label="t('notificationPlan.transports.push')"
              value="push"
              class="mt-5"
            />
            <translation-accordion
              v-if="rawNotificationPlan.push.checked"
              v-slot="{ translation }"
              v-model="rawNotificationPlan.push"
              class="translation-accordion-style"
            >
              <div class="flex flex-col gap-y-5">
                <FormInput
                  v-model="translation.title"
                  type="text"
                  name="push-title"
                  :error="errors?.title"
                  :label="$t('notificationPlanTranslation.title')"
                />
                <TextArea
                  v-model="translation.short"
                  type="text"
                  name="push-short"
                  :error="errors?.short"
                  :label="$t('notificationPlanTranslation.short')"
                />
                <HtmlEditor
                  v-model="translation.message"
                  name="push-message"
                  :error="errors?.message"
                  :label="$t('notificationPlanTranslation.message')"
                />
              </div>
            </translation-accordion>
            <Checkbox
              v-model="rawNotificationPlan.email.checked"
              :label="t('notificationPlan.transports.email')"
              value="email"
              class="mt-5"
            />
            <translation-accordion
              v-if="rawNotificationPlan.email.checked"
              v-slot="{ translation }"
              v-model="rawNotificationPlan.email"
              class="translation-accordion-style"
            >
              <multiselect
                v-model="translation.emailTemplate"
                :label="$t('notificationPlanTransport.email.template')"
                :options="emailTemplateList"
                class="w-full"
                :can-deselect="false"
                :can-clear="false"
                :searchable="true"
              />
            </translation-accordion>
            <Checkbox
              v-model="rawNotificationPlan.zendesk"
              :label="t('notificationPlan.transports.zendesk')"
              value="zendesk"
              class="mt-5"
            />
            <div v-if="rawNotificationPlan.zendesk" class="translation-accordion-style">
              <FormInput
                v-model="rawNotificationPlan.zenDeskSubject"
                type="text"
                name="zendesk-subject"
                class="my-4"
                :error="errors?.zenDeskSubject"
                :label="$t('notificationPlanTranslation.title')"
              />
              <TextArea
                v-model="rawNotificationPlan.zenDeskMessage"
                type="text"
                name="zendesk-message"
                class="my-4"
                :error="errors?.zenDeskMessage"
                :label="$t('notificationPlanTranslation.message')"
              />
            </div>
            <Checkbox
              v-model="rawNotificationPlan.historyEntry"
              :label="t('notificationPlan.transports.historyEntry')"
              value="historyEntry"
              class="mt-5"
            />
            <div v-if="rawNotificationPlan.historyEntry" class="translation-accordion-style">
              <Multiselect
                v-model="rawNotificationPlan.historyEntryType"
                :options="driverCommentTypes"
                class="my-4"
                :error="errors?.historyEntryType"
                :label="$t('pages.driver.historyList.modal.fields.type')"
                :can-deselect="false"
                :can-clear="false"
                :searchable="true"
              />
              <TextArea
                v-model="rawNotificationPlan.historyEntryText"
                type="text"
                name="historyEntryText"
                class="my-4"
                :error="errors?.historyEntryText"
                :label="$t('notificationPlanTranslation.text')"
              />
            </div>
          </div>
        </form-card>
        <form-card
          :title="$t(tKey + '.handlebars.title')"
          :description="$t(tKey + '.handlebars.description')"
        >
          <div class="flex flex-col gap-y-5">
            <div v-for="(handlebar, index) in rawNotificationPlan.customHandlebars" :key="handlebar.id ?? handlebar['@new-id']" class="grid grid-cols-[12rem_1fr_2rem] gap-x-5 border border-light-gray px-5 pb-5 pt-5">
              <FormInput
                v-model="handlebar.key"
                type="text"
                class="mb-4"
                :label="$t('notificationPlan.customHandlebars.key')"
              />
              <translation-accordion
                v-slot="{ translation }"
                v-model="rawNotificationPlan.customHandlebars[index]"
                class="translation-accordion-style"
              >
                <TextArea
                  v-model="translation.value"
                  type="text"
                  :label="$t('notificationPlan.customHandlebars.value')"
                />
              </translation-accordion>
              <RemoveIcon class="w-6 h-6 cursor-pointer" @click="removeHandlebar(handlebar['@id'] ?? handlebar['@new-id'])" />
            </div>
            <div>
              <button
                type="button"
                class="text-body-regular !text-info-blue"
                @click="addHandlebar"
              >
                + {{ t('add') }}
              </button>
            </div>
          </div>
        </form-card>
      </div>
    </div>
    <notification-confirm-modal
      v-model:open="openConfirmModal"
      :number="checkedNumber"
      :show-triggered-warning="triggeredShowWarningForCampaigns"
      :number-array="checkedNumberArray"
      @confirm="onConfirm"
    />
  </modal>
</template>

<script lang="ts" setup>
import {
  computed, ref, unref, watch,
} from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import { object } from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import FormInput from '@utils/inputs/Input.vue';
import Multiselect from '@utils/Multiselect.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import {
  buildOptionsProvider,
  collOptionsProvider,
  enumOptionsProvider,
  type ProviderParams,
} from '@utils/table/hooks/provider';
import { syncRef } from '@hooks/state';
import LogicGate from '@utils/logicGate/LogicGate.vue';
import MultiSwitch from '@utils/inputs/MultiSwitch.vue';
import MultiDatePicker from '@utils/inputs/MultiDatePicker.vue';
import DatePicker from '@utils/inputs/DatePicker.vue';
import MultiselectTags from '@utils/MultiselectTags.vue';
import Switch from '@utils/inputs/Switch.vue';
import Checkbox from '@utils/inputs/Checkbox.vue';
import TranslationAccordion from '@utils/language/TranslationAccordion.vue';
import Slider from '@utils/inputs/Slider.vue';
import TextArea from '@utils/inputs/TextArea.vue';
import CheckFilter from '@utils/logicGate/CheckFilter.vue';
import { parseFilter } from '@utils/logicGate/logicFilter';
import HtmlEditor from '@utils/inputs/HtmlEditor.vue';
import { v4 as uuidv4 } from 'uuid';
import NotificationConfirmModal from '@pages/notifications/NotificationConfirmModal.vue';
import { useCheckFilter } from '@hooks/check-filter';
import RemoveIcon from '@images/remove_filter.svg?component';
import { format } from 'date-fns';
import type {
  Campaign,
  DriverCommentType,
  Filter,
  HydraMember,
  License,
  MaybeHydra,
  NotificationPlan,
  NotificationPlanBase,
  NotificationPlanHandlebar,
  NotificationPlanTransport,
  NotificationTrigger,
} from '@/entities/Entities';
import type { DeepPartial } from '@/utils/types';

interface Props {
  open: boolean;
  notificationPlan?: NotificationPlan;
}

const emit = defineEmits(['update:open', 'update:notificationPlan', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalNotificationPlan = useVModel(props, 'notificationPlan', emit);

const isNew = computed(() => !props.notificationPlan);
const tKey = computed(() =>
  (isNew.value ? 'pages.notificationList.modal.new' : 'pages.notificationList.modal.edit'));

const { t } = useI18n();

const typeSwitchValue = ref<'standard'|'triggered'>('standard');

const typeSwitchOptions = [
  {
    value: 'standard',
    name: t('pages.notificationList.modal.general.typeSwitcher.standard'),
  },
  {
    value: 'triggered',
    name: t('pages.notificationList.modal.general.typeSwitcher.trigger'),
  },
];

const triggeredShowWarningForCampaigns = computed(() => typeSwitchValue.value === 'triggered' && unref(rawNotificationPlan).campaignSwitchValue !== 'no' && noCampaignTriggers.includes(unref(rawNotificationPlan).dateTriggered.trigger));

const notify = useNotify();

// const fetchCampaigns = collOptionsProvider<Campaign>('/campaigns', {}, (value) => ({
//   label: value.name,
//   value: value['@id'],
// }));

const maximumCharacters = (text: string): number => (/^[a-zA-Z0-9_@!$"#éàè&'()*:;+,\<\>\-.\/ ?=äüö%ÜÖÄæÆ\r\n[\[\]{}|^€~\\]*$/.test(text) ? 160 : 70);

const countCharacters = (text: string): number => text.length + (text.match(/[\[\]{}|^€~\\]/g) ?? []).length;

const fetchCampaigns = async (query: string, select: any) => {
  const input = query || select.modelValue?.map((item) => item.split('/')?.slice(-1)?.[0]);

  if (!input) return Promise.resolve([]);
  const filterQuery: ProviderParams = {
    filter: query ? { 'group.name': input } : { 'inArray[id]': JSON.stringify(input) },
  };
  return collOptionsProvider<Campaign>('/campaigns', filterQuery, (value) => ({
    label: value.name,
    value: value['@id'],
  }))();
};

const formatLicense = (value: License | null) => {
  if (!value) return t('licenses.no');
  return `${value.company?.name} (${value.startDate ? format(new Date(value.startDate), 'dd.MM.yyyy') : ''} - ${
    value.endDate ? format(new Date(value.endDate), 'dd.MM.yyyy') : ''
  })`;
};

const fetchLicenses = async (query: string, select: any) => {
  const input = query || select.modelValue?.map((item) => item.split('/')?.slice(-1)?.[0]);

  if (!input) return Promise.resolve([]);
  const filterQuery: ProviderParams = {
    filter: query ? { 'company.name': input } : { 'inArray[id]': JSON.stringify(input) },
  };
  return collOptionsProvider<License>('/licenses', filterQuery, (value) => ({
    label: formatLicense(value),
    value: value['@id'],
  }))();
};

const noCampaignTriggers: NotificationTrigger[] = ['signup', 'mounting_approved', 'profile_complete'];
const withCampaignTriggers: NotificationTrigger[] = ['campaign_registration', 'reward_payout_paid'];
const noLicenseTriggers: NotificationTrigger[] = ['signup', 'mounting_approved', 'profile_complete'];
const withLicenseTriggers: NotificationTrigger[] = ['license_registration', 'license_reward_payout_paid'];

const notificationTriggerOptions = computed(() => {
  if (rawNotificationPlan.value.campaignSwitchValue === 'no') {
    return noCampaignTriggers.map((item) => ({
      label: t(`notificationPlan.dateTriggered.triggerName.${item}`),
      value: item,
    }));
  }
  if (['fix', 'filter'].includes(rawNotificationPlan.value.campaignSwitchValue)) {
    return ([
      {
        label: t('notificationPlan.dateTriggered.campaignConnection'),
        options: withCampaignTriggers.map((item) => ({
          label: t(`notificationPlan.dateTriggered.triggerName.${item}`),
          value: item,
        })),
      },
      {
        label: t('notificationPlan.dateTriggered.noCampaignConnection'),
        options: noCampaignTriggers.map((item) => ({
          label: t(`notificationPlan.dateTriggered.triggerName.${item}`),
          value: item,
        })),
      },
    ]);
  }
  return ([
    {
      label: t('notificationPlan.dateTriggered.licenseConnection'),
      options: withLicenseTriggers.map((item) => ({
        label: t(`notificationPlan.dateTriggered.triggerName.${item}`),
        value: item,
      })),
    },
    {
      label: t('notificationPlan.dateTriggered.noLicenseConnection'),
      options: noLicenseTriggers.map((item) => ({
        label: t(`notificationPlan.dateTriggered.triggerName.${item}`),
        value: item,
      })),
    },
  ]);
});

const updateCampaignSwitchValue = (newValue) => {
  rawNotificationPlan.value.campaignSwitchValue = newValue;
  if (newValue === 'no' && typeSwitchValue.value === 'triggered' && withCampaignTriggers.includes(rawNotificationPlan.value.dateTriggered.trigger)) {
    rawNotificationPlan.value.dateTriggered.trigger = undefined;
  }
};

const driverCommentTypes = collOptionsProvider<DriverCommentType>(
  '/driver_comment_types',
  {
    filter: {
      deleted: false,
    },
    sort: {
      sortKey: 'text',
      sortDirection: 'ASC',
    },
  },
  (value) => ({
    label: value.text,
    value: value['@id'],
  }),
);

interface emailTemplateListType {
  id: string;
  name: string;
}

const emailTemplateList = ref();
const fetchEmailTemplates = async () => {
  emailTemplateList.value = await buildOptionsProvider<
    emailTemplateListType[],
    emailTemplateListType
  >()('/notification_plans/email-templates', (value) => ({
    label: value.name,
    value: value.id,
  }))();
};

watch(internalOpen, (newVal, oldVal) => {
  if (newVal && !oldVal) {
    fetchEmailTemplates();
  }
});

interface CheckedNotificationPlanTransport extends Partial<NotificationPlanTransport> {
  checked: boolean;
}

type campaignSwitchOptionsValue = 'no' | 'fix' | 'filter' | 'fix_license' | 'filter_license';

interface internalNotificationPlanType extends DeepPartial<NotificationPlanBase<'fix' | 'recurrent' | 'event' | 'triggered'>> {
  dateFix: {
    dates: Date[];
  };
  dateRecurrent: {
    startDate?: Date;
    endDate?: Date | null;
    recurrence?: string;
  };
  dateEvent: {
    basePercentage: number;
    modifier?: number;
  };
  dateTriggered: {
    trigger?: NotificationTrigger;
    modifier?: number;
  }
  sms: CheckedNotificationPlanTransport;
  push: CheckedNotificationPlanTransport;
  email: CheckedNotificationPlanTransport;
  zendesk: boolean;
  historyEntry: boolean;
  campaignSwitchValue: campaignSwitchOptionsValue;
  campaigns?: string[];
  licenses?: string[];
}

const initialData = (notificationPlan?: NotificationPlan): internalNotificationPlanType =>
  (notificationPlan
    ? {
      ...notificationPlan,
      receiversFilter: notificationPlan?.receiversFilter ?? {
        logicGroups: [],
      },
      campaignFilter: notificationPlan?.campaignFilter ?? {
        logicGroups: [],
      },
      campaigns: notificationPlan?.campaigns?.map((campaign) => campaign['@id']),
      licenses: notificationPlan?.licenses?.map((license) => license['@id']),
      dateFix: {
        dates: notificationPlan?.dateType === 'fix' ? notificationPlan?.dateData.dates.map((date) => new Date(date)) : [],
      },
      dateRecurrent: {
        startDate: notificationPlan?.dateType === 'recurrent' ? new Date(notificationPlan?.dateData.startDate) : undefined,
        endDate: notificationPlan?.dateType === 'recurrent' && notificationPlan?.dateData.endDate
          ? new Date(notificationPlan?.dateData.endDate) : undefined,
        recurrence: notificationPlan?.dateType === 'recurrent' ? notificationPlan?.dateData.recurrence : undefined,
      },
      dateType: notificationPlan?.dateType === 'triggered' ? 'fix' : notificationPlan?.dateType,
      dateEvent: {
        basePercentage: notificationPlan?.dateType === 'event' ? notificationPlan?.dateData.basePercentage : 0,
        modifier: notificationPlan?.dateType === 'event' ? notificationPlan?.dateData.modifier : 0,
      },
      dateTriggered: {
        trigger: notificationPlan?.dateType === 'triggered' ? notificationPlan?.dateData.trigger : undefined,
        modifier: notificationPlan?.dateType === 'triggered' ? notificationPlan?.dateData.modifier : 0,
      },
      sms: notificationPlan?.sms
        ? {
          checked: true,
          ...notificationPlan.sms,
        }
        : {
          checked: false,
          translations: [],
        },
      push: notificationPlan?.push
        ? {
          checked: true,
          ...notificationPlan.push,
        }
        : {
          checked: false,
          translations: [],
        },
      email: notificationPlan?.email
        ? {
          checked: true,
          ...notificationPlan.email,
        }
        : {
          checked: false,
          translations: [],
        },
      zendesk: notificationPlan?.zenDeskSubject !== null,
      historyEntry: notificationPlan?.historyEntryText !== null && notificationPlan?.historyEntryType !== null,
      historyEntryType: notificationPlan?.historyEntryType?.['@id'] ?? undefined,
      campaignSwitchValue: notificationPlan?.campaignConnection ?? 'no',
    }
    : {
      name: undefined,
      campaigns: undefined,
      licenses: undefined,
      campaignConnection: undefined,
      receiversFilter: {
        logicGroups: [],
      },
      campaignFilter: {
        logicGroups: [],
      },
      active: undefined,
      dateFix: {
        dates: [],
      },
      dateType: 'fix',
      dateRecurrent: {
        startDate: undefined,
        endDate: undefined,
        recurrence: undefined,
      },
      dateEvent: {
        basePercentage: 0,
        modifier: undefined,
      },
      dateTriggered: {
        trigger: undefined,
        modifier: undefined,
      },
      sms: {
        checked: false,
        translations: [],
      },
      push: {
        checked: false,
        translations: [],
      },
      email: {
        checked: false,
        translations: [],
      },
      zendesk: false,
      zenDeskSubject: undefined,
      zenDeskMessage: undefined,
      historyEntry: false,
      historyEntryText: undefined,
      historyEntryType: undefined,
      campaignSwitchValue: 'no',
      customHandlebars: [],
    });

const rawNotificationPlan = syncRef(internalNotificationPlan, (notificationPlan) => ({
  ...initialData(notificationPlan),
}));

watch(internalNotificationPlan, (newVal) => {
  typeSwitchValue.value = newVal?.dateType === 'triggered' ? 'triggered' : 'standard';
});

const {
  data: notificationPlan,
  errors,
  validatedFn,
  setValidation,
} = useValidation(object({}), rawNotificationPlan);

function resetData() {
  rawNotificationPlan.value = { ...initialData() } as unknown as typeof notificationPlan['value'];
  setValidation(false);
  driverCheckWarning.value = undefined;
  typeSwitchValue.value = 'standard';
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalNotificationPlan.value = undefined;
}

interface campaignSwitchOption {
  value: campaignSwitchOptionsValue;
  name: string;
}
const campaignSwitchOptions = computed<campaignSwitchOption[]>(() => [
  {
    value: 'no',
    name: t('notificationPlan.campaignSwitch.type.no'),
  },
  {
    value: 'fix',
    name: t('notificationPlan.campaignSwitch.type.fix'),
  },
  {
    value: 'filter',
    name: t('notificationPlan.campaignSwitch.type.filter'),
  },
  {
    value: 'fix_license',
    name: t('notificationPlan.campaignSwitch.type.fix_license'),
  },
  {
    value: 'filter_license',
    name: t('notificationPlan.campaignSwitch.type.filter_license'),
  },
]);

type dateSwitchOptionsValue = 'fix' | 'recurrent' | 'event';
interface dateSwitchOption {
  value: dateSwitchOptionsValue;
  name: string;
}
const dateSwitchOptions = computed<dateSwitchOption[]>(() => [
  {
    value: 'fix',
    name: t('notificationPlan.dateSwitch.type.fix'),
  },
  {
    value: 'recurrent',
    name: t('notificationPlan.dateSwitch.type.recurrent'),
  },
  ...(['fix', 'filter'].includes(rawNotificationPlan?.value?.campaignSwitchValue)
    ? ([
      {
        value: 'event',
        name: t('notificationPlan.dateSwitch.type.event'),
      },
    ] as dateSwitchOption[])
    : []),
]);

const dateSwitchValue = computed({
  get() {
    if (rawNotificationPlan?.value?.campaignSwitchValue === 'no' && rawNotificationPlan?.value?.dateType === 'event') {
      return 'fix';
    }
    return rawNotificationPlan?.value?.dateType;
  },
  set(newVal) {
    rawNotificationPlan.value.dateType = newVal;
  },
});

const [exec, { loading }] = useFetchAction();

const replaceIdWithAtId = <T extends Partial<HydraMember> | undefined>(obj: T): T => ({
  ...obj,
  id: obj?.['@id'] ?? null,
});

const replaceIdWithAtIdInArray = <S extends Partial<HydraMember> | undefined>(arr: S[] | undefined): S[] | undefined =>
  arr?.map((obj) => replaceIdWithAtId(obj));

const driverFilter = ref<LogicGate>(null);

const campaignFilter = ref<LogicGate>(null);

const checkDriverFilterAdditionalPayload = computed(() => {
  if (rawNotificationPlan.value.campaignSwitchValue === 'fix') {
    return {
      dependentOnCampaigns: rawNotificationPlan.value.campaigns?.map((item) => item.replace('/campaigns/', '')) ?? [],
      notificationPlanId: rawNotificationPlan.value.id ?? null,
    };
  }
  if (rawNotificationPlan.value.campaignSwitchValue === 'filter') {
    return {
      dependentOnCampaignFilter: rawNotificationPlan.value.campaignFilter,
      notificationPlanId: rawNotificationPlan.value.id ?? null,
    };
  }
  if (rawNotificationPlan.value.campaignSwitchValue === 'fix_license') {
    return {
      dependentOnLicenses: rawNotificationPlan.value.licenses?.map((item) => item.replace('/licenses/', '')) ?? [],
      notificationPlanId: rawNotificationPlan.value.id ?? null,
    };
  }
  if (rawNotificationPlan.value.campaignSwitchValue === 'filter_license') {
    return {
      dependentOnLicensesFilter: rawNotificationPlan.value.campaignFilter,
      notificationPlanId: rawNotificationPlan.value.id ?? null,
    };
  }
  // rawNotificationPlan.value.campaignSwitchValue === 'no'
  return {
    notificationPlanId: rawNotificationPlan.value.id ?? null,
  };
});

const driverCheckWarning = ref<string | undefined>(undefined);

const driverFilterVerification = (data: MaybeHydra<Filter>): boolean => {
  if (['fix', 'filter'].includes(rawNotificationPlan.value.campaignSwitchValue)) {
    if (driverFilter.value.checkForUnconditional(data)) {
      driverCheckWarning.value = t('pages.notificationList.modal.driverFilter.unconditionalWarning');
    }
  }
  if (['fix_license', 'filter_license'].includes(rawNotificationPlan.value.campaignSwitchValue)) {
    if (driverFilter.value.checkForUnconditional(data)) {
      driverCheckWarning.value = t('pages.notificationList.modal.driverFilter.unconditionalLicenseWarning');
    }
  }
  if (rawNotificationPlan.value.campaignSwitchValue === 'filter') {
    if (campaignFilter.value?.validateData(rawNotificationPlan.value.campaignFilter)) {
      return driverFilter.value?.validateData(data);
    }
    return false;
  }
  return driverFilter.value?.validateData(data);
};

const openConfirmModal = ref(false);
const {
  checkFilter,
  checkedNumber,
  checkedNumberArray,
  loading: checkFilterLoading,
} = useCheckFilter(
  '/filters/notification-driver',
  computed(() => rawNotificationPlan.value.receiversFilter),
);

const campaignReferenceTKey = computed(() => {
  let key = '';
  if (rawNotificationPlan.value.campaignSwitchValue === 'no') {
    key = 'noReference';
  } else if (['fix', 'filter'].includes(rawNotificationPlan.value.campaignSwitchValue)) {
    key = 'campaign';
  } else if (['fix_license', 'filter_license'].includes(rawNotificationPlan.value.campaignSwitchValue)) {
    key = 'license';
  }
  if (typeSwitchValue.value === 'triggered') {
    key += 'Triggered';
  }
  return key;
});

const save = validatedFn(async () => {
  if (
    !notificationPlan.value.receiversFilter ||
    !driverFilter.value?.validateData(notificationPlan.value.receiversFilter)
  ) {
    return;
  }

  if (
    rawNotificationPlan.value.campaignSwitchValue === 'filter' &&
    (!notificationPlan.value.campaignFilter ||
      !campaignFilter.value?.validateData(notificationPlan.value.campaignFilter))
  ) {
    return;
  }

  if (typeSwitchValue.value !== 'triggered') {
    await checkFilter(driverFilterVerification, checkDriverFilterAdditionalPayload.value);
  } else if (!triggeredShowWarningForCampaigns.value) {
    await onConfirm();
    return;
  }

  openConfirmModal.value = true;
});

const onConfirm = async () => {
  try {
    const edit = !!props.notificationPlan;
    const url = edit ? props.notificationPlan['@id'] : '/notification_plans';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        name: notificationPlan.value.name,
        active: notificationPlan.value.active,
        receiversFilter: parseFilter(notificationPlan.value.receiversFilter),
        ...(rawNotificationPlan?.value?.campaignSwitchValue === 'fix'
          ? {
            campaigns: notificationPlan.value.campaigns,
          } : {}),
        ...(rawNotificationPlan?.value?.campaignSwitchValue === 'fix_license'
          ? {
            licenses: notificationPlan.value.licenses,
          } : {}),
        // note: campaignFilter is used for both campaign and license filters
        campaignFilter: ['filter', 'filter_license'].includes(rawNotificationPlan?.value?.campaignSwitchValue)
          ? parseFilter(notificationPlan.value.campaignFilter)
          : null,
        campaignConnection: rawNotificationPlan?.value?.campaignSwitchValue,
        dateType: typeSwitchValue.value === 'triggered' ? 'triggered' : dateSwitchValue.value,
        dateData: typeSwitchValue.value === 'triggered'
          ? notificationPlan.value.dateTriggered : dateSwitchValue.value === 'fix'
            ? notificationPlan.value.dateFix : dateSwitchValue.value === 'recurrent'
              ? notificationPlan.value.dateRecurrent : notificationPlan.value.dateEvent,
        sms: notificationPlan.value.sms.checked
          ? replaceIdWithAtId({
            ...notificationPlan.value.sms,
            translations: replaceIdWithAtIdInArray(notificationPlan.value.sms.translations?.filter((item) => item.title || item.message)),
          }) : null,
        push: notificationPlan.value.push.checked
          ? replaceIdWithAtId({
            ...notificationPlan.value.push,
            translations: replaceIdWithAtIdInArray(notificationPlan.value.push.translations?.filter((item) => item.title || item.message || item.short)),
          }) : null,
        email: notificationPlan.value.email.checked
          ? replaceIdWithAtId({
            ...notificationPlan.value.email,
            translations: replaceIdWithAtIdInArray(notificationPlan.value.email.translations?.filter((item) => item.emailTemplate)),
          }) : null,
        zenDeskSubject: notificationPlan.value.zendesk ? (notificationPlan.value.zenDeskSubject ?? '') : null,
        zenDeskMessage: notificationPlan.value.zendesk ? (notificationPlan.value.zenDeskMessage ?? '') : null,
        historyEntryText: notificationPlan.value.historyEntry ? (notificationPlan.value.historyEntryText) : null,
        historyEntryType: notificationPlan.value.historyEntry ? (notificationPlan.value.historyEntryType) : null,
        customHandlebars: notificationPlan.value.customHandlebars?.map((item) => ({ ...item, id: item['@id'] ?? undefined, translations: replaceIdWithAtIdInArray(item?.translations ?? []) })),
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
};

const addHandlebar = () => {
  if (undefined === rawNotificationPlan.value.customHandlebars) {
    rawNotificationPlan.value.customHandlebars = [];
  }
  rawNotificationPlan.value.customHandlebars?.push({
    key: '',
    translations: [],
    '@new-id': uuidv4(),
  });
};

const removeHandlebar = (id: string) => {
  rawNotificationPlan.value.customHandlebars = rawNotificationPlan.value.customHandlebars?.filter((item: NotificationPlanHandlebar) => item['@id'] !== id && item['@new-id'] !== id);
};
</script>

<style lang="scss" scoped>
.translation-accordion-style {
  @apply px-6 py-5;
  @apply mb-5 ml-2;
  @apply border-l border-l-element-neutral border-b border-b-element-neutral;
}
</style>
